import { z } from 'zod'

import { lenientList, moneySchema } from '@/schemas/common'

/**
 * Market · Sectors — the heat strip, the turnover donut and the seven cards.
 *
 * The three blocks describe the same seven groups but not with the same
 * figures: the strip is sized by turnover and coloured by change, the donut
 * splits one day's rupees, and a card adds index, volume and breadth. One
 * payload keeps them consistent, and the `id` on every block is the kebab
 * sector id the rest of the app filters on (`?sector=hydropower`).
 */

export const SECTOR_VIEWS = ['change', 'turnover', 'az'] as const

export const sectorViewSchema = z.enum(SECTOR_VIEWS)

/** One tile of the strip. Width is turnover, colour is change. */
export const sectorHeatSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  percentChange: moneySchema,
  /** `null` when mm-be reports no turnover figure for this sector. */
  turnoverArba: moneySchema.nullable(),
})

/**
 * The donut. `percent` is a number, not `Money`: it is a slice angle first and
 * a printed figure second, and a charting library takes numbers — the same
 * exemption `IndexPoint.value` already carries.
 */
export const turnoverShareSchema = z.object({
  totalArba: moneySchema,
  count: z.number().int(),
  slices: lenientList(
    z.object({
      /** The sector id the table rows carry, so a slice and its rows can be tied together. */
      id: z.string().min(1),
      name: z.string().min(1),
      percent: z.number(),
    }),
    'sector share',
  ),
  caption: z.string().min(1),
})

export const sectorCardSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  percentChange: moneySchema,
  /** The sector index level: "1,290.40". */
  index: moneySchema,
  /** `null` when mm-be reports no volume for this sector (a fund group trades none). */
  volumeKitta: z.number().int().nullable(),
  /** Rupees in crore. The card prints Arba above 100. `null` when mm-be
   *  reports no turnover figure for this sector. */
  turnoverCr: moneySchema.nullable(),
  rose: z.number().int(),
  fell: z.number().int(),
})

export const sectorsOverviewSchema = z.object({
  /*
   * Both lists drop an unreadable row rather than fail: the rows come from
   * one feed, and a single sector with a missing figure must not blank the
   * other twelve.
   */
  heat: lenientList(sectorHeatSchema, 'sector heat'),
  /** The line under the strip explaining what its width means. */
  caption: z.string().min(1),
  share: turnoverShareSchema,
  cards: lenientList(sectorCardSchema, 'sector card'),
  mitraSays: z.string().min(1),
  explain: z.array(z.string().min(1)),
})

/**
 * One metric across a sector's companies — `/market/sectors/{symbol}/stats/`,
 * the "vs sector median" figure on the Stock page.
 *
 * `metric` is mm-be's snake_case code (`pe_ratio`, `roe`, `market_cap`, …),
 * the same code `/company/compare/`'s `leads` use, so it is the join key.
 * `reporting` is how many companies REPORTED this metric — not how many are in
 * the sector — and every figure is null when none did. Each company counts
 * once, at its newest row; a median over few reporters deserves a quieter
 * claim than one over many.
 *
 * Computed by mm-be from its OWN fundamentals tables, not the external data
 * service, so these are populated where a company's statement is empty.
 */
export const sectorMetricSchema = z.object({
  metric: z.string().min(1),
  label: z.string().min(1),
  median: moneySchema.nullable(),
  average: moneySchema.nullable(),
  low: moneySchema.nullable(),
  high: moneySchema.nullable(),
  reporting: z.number().int().min(0),
})

/**
 * A sector's medians. `sector` echoes the sector SYMBOL asked for; `count` is
 * how many active companies the sector has — context, not a denominator.
 */
export const sectorStatsSchema = z.object({
  sector: z.string().min(1),
  count: z.number().int().min(0),
  metrics: lenientList(sectorMetricSchema, 'sector metric'),
})

export type SectorView = z.infer<typeof sectorViewSchema>
export type SectorHeat = z.infer<typeof sectorHeatSchema>
export type TurnoverShare = z.infer<typeof turnoverShareSchema>
export type SectorCard = z.infer<typeof sectorCardSchema>
export type SectorsOverview = z.infer<typeof sectorsOverviewSchema>
export type SectorMetric = z.infer<typeof sectorMetricSchema>
export type SectorStats = z.infer<typeof sectorStatsSchema>
